import { useEffect } from 'react';
import { useTimeSeriesReducer } from '@/components/TimeSeries/hooks';
import { useTheme } from '@simplywallst/ui-core';
import { TimeSeries } from '../../TimeSeries';
import { toTruncated } from '@/utilities/formatting';
import useResizeObserver from 'use-resize-observer';
import { computeColumns, StackBars } from '../../StackBars';
import {
  normalizeByMonthlySumWithTimestamp,
  calcChartInfo,
  convertTimeseriesDataToStackData,
} from './utils';
import type { Series } from './types';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import { useIsSmallScreen } from '@/hooks/useMedia';
import { usePortfolioDividendEfpCreator } from '../../../hooks/usePortfolioDividendEfp';
import type { Portfolio } from '@/hooks/portfolio/usePortfolio';
import type { DemoPortfolio } from '@/hooks/portfolio/useDemoPortfolio';
import { getTimeseriesByType } from '../../../utils/extractPortfolioDividendEfp';

interface Props {
  selectedColumnKey?: string;
  onSelectColumn?: (key: string) => void;
  portfolio: Portfolio | DemoPortfolio;
}

const KEYS = {
  declared: 'declared',
  estimated: 'estimated',
} as const;

const defaultFilter = {
  [KEYS.declared]: true,
  [KEYS.estimated]: true,
};

export const BarChart = ({
  selectedColumnKey,
  onSelectColumn,
  portfolio,
}: Props) => {
  const theme = useTheme();
  const { id: portfolioId, currencyISO } = portfolio;

  const { data: rawData } = usePortfolioDividendEfpCreator(currencyISO)({
    portfolioId,
  });

  const seriesData = getTimeseriesByType(rawData);

  const data: Record<Series, number[][]> = {
    [KEYS.declared]: normalizeByMonthlySumWithTimestamp(seriesData.declared),
    [KEYS.estimated]: normalizeByMonthlySumWithTimestamp(seriesData.estimated),
  };

  const chartInfo = calcChartInfo(data);

  const dayZero = chartInfo.dayZero;

  const { entities, keys, series, filter, disabled, dispatch } =
    useTimeSeriesReducer<Series>({
      data,
      dayZero,
      defaultFilter,
      hideToggleFilter: false,
    });

  const colors: Record<Series, string> = {
    [KEYS.declared]: theme.color.chart02,
    [KEYS.estimated]: theme.color.chart03,
  };

  const seriesWithLabels = [
    { key: KEYS.declared, label: 'Declared' },
    { key: KEYS.estimated, label: 'Estimated' },
  ];

  useEffect(
    () =>
      dispatch({
        type: 'reset',
        payload: {
          data,
          dayZero,
          defaultFilter,
        },
      }),
    [dispatch]
  );

  useEffect(() => {
    onSelectColumn?.(keys[0].toString());
  }, [keys, onSelectColumn]);

  const { ref, width: rawWidth } = useResizeObserver<HTMLDivElement>();
  const width = rawWidth || 0;
  const { isMobile } = useUserAgent();
  const isSmallScreen = useIsSmallScreen({ defaultMatches: isMobile });
  const height = isSmallScreen ? 150 : 250;

  return (
    <div ref={ref}>
      <TimeSeries<Series>
        width={width}
        height={height}
        entities={entities}
        keys={keys}
        filter={filter}
        disabled={disabled}
        maximumX={chartInfo.maxX}
        maximumY={chartInfo.maxY}
        minimumX={chartInfo.minX}
        minimumY={chartInfo.minY > 0 ? 0 : chartInfo.minY}
        series={seriesWithLabels}
        seriesColorSelect={(series) => colors[series]}
        formatYAxis={(value) => toTruncated(value, 0)}
        noOfColumns={chartInfo.chartColumnsCap}
        onLegendClick={(series) => {
          dispatch({
            type: 'filter',
            payload: {
              key: series,
              data,
            },
          });
        }}
      >
        {({ xScale, yScale, keys, entities, contentWidth, contentHeight }) => {
          const barKeys = series.filter((n) => filter[n]);

          const stackedEntities = convertTimeseriesDataToStackData({
            seriesKeys: keys,
            barKeys,
            entities,
          });

          const stackBarData = computeColumns({
            columnKeys: keys,
            barKeys,
            entities: stackedEntities,
            xScale,
            yScale,
            chartWidth: contentWidth,
            colors,
            chartColumnsCap: chartInfo.chartColumnsCap,
          });

          return (
            <StackBars
              data={stackBarData}
              showColumnKey={false}
              height={contentHeight}
              hoveringColumnKey={selectedColumnKey}
              onHover={onSelectColumn}
            />
          );
        }}
      </TimeSeries>
    </div>
  );
};
